// src/components/PublicReferees.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './TableList.css';
import Footer from './Footer';
import './PublicAthletes.css'; // pentru badge-legitimatie & chips

export default function PublicReferees() {
  const navigate = useNavigate();
  const [referees, setReferees] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('status-active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();

  const years = useMemo(
    () => [currentYear - 1, currentYear, currentYear + 1],
    [currentYear]
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);


  const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';
  const isActive = (r) => !!(r && toBool(r.is_legitimated));
  const norm = (s) =>
    (s ?? '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');

  const fetchReferees = async (clubName = '', year = selectedYear) => {
    setLoading(true);
    setError(null);
    try {
      let url = `https://www.fro.ro/api/referees/show-referees.php?year=${year}`;
      if (clubName) url += `&club=${encodeURIComponent(clubName)}`;

      const res = await fetch(url, { cache: 'no-store' });
      const data = await res.json();

      if (data.success) {
        const list = data.referees || [];
        setReferees(list);

        if (!clubName) {
          const uniqueClubs = [...new Set(list.map(a => a.club_name).filter(Boolean))];
          uniqueClubs.sort((a, b) => a.localeCompare(b, 'ro'));
          setClubs(uniqueClubs);
        }
      } else {
        setReferees([]);
        setError(data.message || 'Eroare la încărcarea arbitrilor');
      }
    } catch {
      setError('Eroare la conexiune');
      setReferees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferees('', selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const handleClubChange = (e) => {
    const club = e.target.value;
    setSelectedClub(club);
    fetchReferees(club, selectedYear);
  };

  const handleYearChange = (e) => setSelectedYear(Number(e.target.value));

  const query = norm(search);
  const filteredReferees = referees.filter((r) => {
    const fullNameNorm = norm(`${r.first_name || ''} ${r.last_name || ''}`.trim());
    const birthStr = String(r.birth_year || '');
    const matchesSearch =
      query === '' || fullNameNorm.includes(query) || birthStr.includes(search.trim());
    const matchesClub = selectedClub === '' || r.club_name === selectedClub;
    return matchesSearch && matchesClub;
  });

  const sortedReferees = [...filteredReferees].sort((a, b) => {
    const aActive = isActive(a) ? 1 : 0;
    const bActive = isActive(b) ? 1 : 0;

    switch (sortBy) {
      case 'birth_year-asc':
        return (a.birth_year || 0) - (b.birth_year || 0);
      case 'birth_year-desc':
        return (b.birth_year || 0) - (a.birth_year || 0);
      case 'status-active':
        return bActive - aActive;
      case 'status-inactive':
        return aActive - bActive;
      case 'name-az':
      default: {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();

        return nameA.localeCompare(nameB, 'ro');
      }
    }
  });

  const sublineStyle = { display: 'block', marginTop: 4, fontSize: 12, color: '#666' };

  return (
    <>
      <div className="athletes-container">
        <button onClick={() => navigate('/')} className="back-button">
          <ArrowLeft size={18} strokeWidth={2.2} />
        </button>
        <h1 className="title">Arbitri</h1>
      </div>

      <div className="table-list-container">
        <div className="table-list-header">
          <div className="sort-bar">
            <label htmlFor="sort-select" className="sort-label">Sortare:</label>
            <select
              id="sort-select"
              className="sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="name-az">A–Z după nume</option>
              <option value="birth_year-asc">An naștere ↑</option>
              <option value="birth_year-desc">An naștere ↓</option>
              <option value="status-active">Activi întâi</option>
              <option value="status-inactive">Inactivi întâi</option>
            </select>
          </div>

          <div className="sort-bar" style={{ width: '20%' }}>
            <label htmlFor="club-select" className="sort-label">Filtrează după club:</label>
            <select
              id="club-select"
              className="sort-select"
              value={selectedClub}
              onChange={handleClubChange}
            >
              <option value="">Toate cluburile</option>
              {clubs.map((club, idx) => (
                <option key={idx} value={club}>{club}</option>
              ))}
            </select>
          </div>


          <div className="sort-bar">
            <label htmlFor="year-select" className="sort-label">An:</label>
            <select
              id="year-select"
              className="sort-select"
              value={selectedYear}
              onChange={handleYearChange}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y === currentYear ? `${y} (curent)` : `${y}`}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
            <input
              type="text"
              className="search-bar"
              placeholder="Caută după nume sau an naștere (ex: 1987)"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <table className="table-list">
          <thead>
            <tr>
              <th>Nr. legitimație</th>
              <th>Nume</th>
              <th>An naștere</th>
              <th>Club</th>
              <th>Categorie</th>
              <th>Activ</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                  Se încarcă arbitrii...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'crimson' }}>
                  {error}
                </td>
              </tr>
            ) : sortedReferees.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                  Nu există arbitri pentru filtrul selectat.
                </td>
              </tr>
            ) : (
              sortedReferees.map((r, idx) => {
                const statusText = isActive(r) ? 'Activ' : 'Inactiv';
                return (
                  <tr key={idx}>
                    <td>
                      {r.license_number && r.license_number.trim() !== '' ? (
                        <span className="badge-legitimatie">{r.license_number}</span>
                      ) : (
                        <span className="badge-legitimatie empty">-</span>
                      )}
                    </td>
                    <td>
                      <span>{r.first_name} {r.last_name}</span>

                      <span style={sublineStyle}>{statusText}</span>
                    </td>
                    <td>{r.birth_year || '-'}</td>
                    <td>{r.club_name || '-'}</td>
                    <td>{r.categorie || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {isActive(r) ? (
                        <span className="leg-chip leg-chip--on" aria-label="Activ" title="Activ">
                          <svg width="14" height="14" viewBox="0 0 24 24">
                            <rect x="1.5" y="1.5" width="21" height="21" rx="4" fill="#111" />
                            <path d="M6 12.5l4 4 8-9" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="leg-chip leg-chip--off" aria-label="Inactiv" title="Inactiv">
                          <svg width="14" height="14" viewBox="0 0 24 24">
                            <rect x="1.5" y="1.5" width="21" height="21" rx="4" fill="#f1f1f1" stroke="#ccc" />
                          </svg>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
